import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import Ornament from "@/components/Ornament";
import SeoHead from "@/components/SeoHead";
import FetesMap, { matchFetesLocation } from "@/components/FetesMap";
import api from "@/lib/api";

const L10N = {
  fr: { kicker: "Le temps du Prieuré", title: "Agenda des événements", today: "Aujourd'hui", fetes: "Fêtes · Juillet 2027", empty: "Aucun événement ce jour.", pick: "Choisissez un jour dans le calendrier.", events: "événement(s)", dayPdf: "Programme du jour (PDF)", onMap: "Voir sur la carte", mapTitle: "Sur la carte de la Baie" },
  en: { kicker: "The Priory's time", title: "Events calendar", today: "Today", fetes: "Feasts · July 2027", empty: "No event on this day.", pick: "Pick a day in the calendar.", events: "event(s)", dayPdf: "Day programme (PDF)", onMap: "See on the map", mapTitle: "On the Bay map" },
  it: { kicker: "Il tempo del Priorato", title: "Agenda degli eventi", today: "Oggi", fetes: "Feste · Luglio 2027", empty: "Nessun evento in questo giorno.", pick: "Scegli un giorno nel calendario.", events: "evento/i", dayPdf: "Programma del giorno (PDF)", onMap: "Vedi sulla mappa", mapTitle: "Sulla mappa della Baia" },
  es: { kicker: "El tiempo del Priorato", title: "Agenda de eventos", today: "Hoy", fetes: "Fiestas · Julio 2027", empty: "Ningún evento este día.", pick: "Elija un día en el calendario.", events: "evento(s)", dayPdf: "Programa del día (PDF)", onMap: "Ver en el mapa", mapTitle: "En el mapa de la Bahía" },
  de: { kicker: "Die Zeit des Priorats", title: "Veranstaltungskalender", today: "Heute", fetes: "Feste · Juli 2027", empty: "Keine Veranstaltung an diesem Tag.", pick: "Wählen Sie einen Tag im Kalender.", events: "Veranstaltung(en)", dayPdf: "Tagesprogramm (PDF)", onMap: "Auf der Karte ansehen", mapTitle: "Auf der Karte der Bucht" },
  pt: { kicker: "O tempo do Priorado", title: "Agenda de eventos", today: "Hoje", fetes: "Festas · Julho 2027", empty: "Nenhum evento neste dia.", pick: "Escolha um dia no calendário.", events: "evento(s)", dayPdf: "Programa do dia (PDF)", onMap: "Ver no mapa", mapTitle: "No mapa da Baía" },
};

const SEO_TITLES = {
  fr: "Agenda — Prieuré saint Léonard",
  en: "Calendar — Priory of Saint Léonard",
};
const SEO_DESC = {
  fr: "Calendrier des offices, concerts, conférences et Fêtes populaires 2027 du Prieuré saint Léonard de Vains.",
  en: "Calendar of services, concerts, lectures and 2027 Popular Feasts of the Priory of Saint Léonard, Vains.",
};

const iso = (d) => d.toISOString().slice(0, 10);
const dayUTC = (s) => new Date(`${s}T12:00:00Z`);
const addDays = (s, n) => {
  const d = dayUTC(s);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
};

// Expand base ranges + daily/weekly recurrences into a { "YYYY-MM-DD": [event] } map for one month.
function occurrencesForMonth(events, monthStart, monthEnd) {
  const map = {};
  const push = (day, ev) => {
    if (day < monthStart || day > monthEnd) return;
    (map[day] = map[day] || []).push(ev);
  };
  for (const ev of events) {
    if (!ev.date) continue;
    const end = ev.end_date && ev.end_date > ev.date ? ev.end_date : ev.date;
    for (let d = ev.date; d <= end && d <= monthEnd; d = addDays(d, 1)) push(d, ev);
    if (ev.recurrence === "daily" || ev.recurrence === "weekly") {
      const step = ev.recurrence === "daily" ? 1 : 7;
      const until = ev.recurrence_until && ev.recurrence_until < monthEnd ? ev.recurrence_until : monthEnd;
      for (let d = addDays(ev.date, step); d <= until; d = addDays(d, step)) push(d, ev);
    }
  }
  for (const day of Object.keys(map)) map[day].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  return map;
}

export default function Agenda() {
  const { t, locale } = useI18n();
  const l = L10N[locale] || L10N.fr;
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });
  const [selected, setSelected] = useState(() => iso(new Date()));
  const [mapFocus, setMapFocus] = useState(null);

  useEffect(() => { setMapFocus(null); }, [selected]);

  useEffect(() => {
    api.get("/events", { params: { include_past: true } })
      .then((r) => setEvents(r.data || []))
      .catch(() => {});
  }, []);

  const monthStart = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-01`;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const monthEnd = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;
  const byDay = useMemo(() => occurrencesForMonth(events, monthStart, monthEnd), [events, monthStart, monthEnd]);

  const firstWeekday = (dayUTC(monthStart).getUTCDay() + 6) % 7; // Monday = 0
  const cells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => addDays(monthStart, i)),
  ];
  const todayIso = iso(new Date());
  const dateLocale = locale === "fr" ? "fr-FR" : locale;
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(dateLocale, { month: "long", year: "numeric" });
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Date(2026, 5, 1 + i).toLocaleDateString(dateLocale, { weekday: "short" })
  );

  const move = (delta) => setCursor(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const selectedEvents = byDay[selected] || [];
  const isFetesDay = selected >= "2027-07-01" && selected <= "2027-07-05" && selectedEvents.some((e) => (e.tags || []).includes("fetes_2027"));
  const selectedLabel = selected
    ? dayUTC(selected).toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div data-testid="agenda-page" className="pt-28 pb-24" style={{ background: "var(--parchment)" }}>
      <SeoHead titles={SEO_TITLES} descriptions={SEO_DESC}
        keywords="agenda événements Normandie, concerts baie du Mont-Saint-Michel, offices, conférences, calendrier Fêtes 2027, Prieuré saint Léonard, Vains, Avranches" />
      <div className="container-prieure">
        <div className="max-w-3xl mb-10">
          <div className="font-caps text-[0.72rem] mb-4" style={{ color: "var(--cinnabar)" }}>{l.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "var(--ink)" }}>
            {l.title}
          </h1>
          <Ornament className="justify-start mt-5" />
        </div>

        {/* Month navigation */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button onClick={() => move(-1)} data-testid="agenda-prev" aria-label="previous month"
                  className="px-4 py-2 border font-display text-lg" style={{ borderColor: "var(--border)", color: "var(--ink)" }}>‹</button>
          <div className="font-display text-2xl sm:text-3xl capitalize min-w-[220px] text-center" data-testid="agenda-month-label" style={{ color: "var(--ink)" }}>
            {monthLabel}
          </div>
          <button onClick={() => move(1)} data-testid="agenda-next" aria-label="next month"
                  className="px-4 py-2 border font-display text-lg" style={{ borderColor: "var(--border)", color: "var(--ink)" }}>›</button>
          <button onClick={() => { const n = new Date(); setCursor({ y: n.getFullYear(), m: n.getMonth() }); setSelected(todayIso); }}
                  data-testid="agenda-today"
                  className="ml-2 px-4 py-2 border font-caps text-[0.62rem]" style={{ borderColor: "var(--border)", color: "var(--ink-2)", letterSpacing: "0.18em" }}>
            {l.today.toUpperCase()}
          </button>
          <button onClick={() => { setCursor({ y: 2027, m: 6 }); setSelected("2027-07-01"); }}
                  data-testid="agenda-jump-fetes"
                  className="px-4 py-2 border font-caps text-[0.62rem]" style={{ borderColor: "var(--gold)", color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
            ◇ {l.fetes.toUpperCase()} ◇
          </button>
        </div>

        {/* Calendar grid */}
        <div data-testid="agenda-grid" className="bg-white border" style={{ borderColor: "var(--border)" }}>
          <div className="grid grid-cols-7 border-b" style={{ borderColor: "var(--border)" }}>
            {weekdays.map((w) => (
              <div key={w} className="py-2 text-center font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                {w.toUpperCase()}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              if (!day) return <div key={`b${i}`} style={{ background: "var(--parchment-2)", minHeight: 92 }} />;
              const evs = byDay[day] || [];
              const isFetes = evs.some((e) => (e.tags || []).includes("fetes_2027"));
              const isSel = day === selected;
              const isToday = day === todayIso;
              return (
                <button
                  key={day}
                  data-testid={`agenda-day-${day}`}
                  onClick={() => setSelected(day)}
                  className="text-left p-1.5 sm:p-2 border-b border-r transition-colors align-top"
                  style={{
                    borderColor: "var(--border)",
                    minHeight: 92,
                    background: isSel ? "var(--ink)" : isFetes ? "rgba(212,175,55,0.16)" : "transparent",
                    outline: isToday && !isSel ? "2px solid var(--cinnabar)" : "none",
                    outlineOffset: -2,
                  }}
                >
                  <span className="font-display text-sm sm:text-base" style={{ color: isSel ? "var(--gold)" : "var(--ink)" }}>
                    {Number(day.slice(8, 10))}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {evs.slice(0, 2).map((e) => (
                      <div key={`${e.id}-${day}`} className="truncate text-[0.62rem] sm:text-[0.68rem] px-1 py-0.5"
                           style={{
                             background: isSel ? "rgba(212,175,55,0.25)" : "var(--parchment-2)",
                             color: isSel ? "#fff" : "var(--ink-2)",
                             borderLeft: `2px solid ${(e.tags || []).includes("fetes_2027") ? "var(--gold)" : "var(--cinnabar)"}`,
                           }}>
                        {e.time ? `${e.time} · ` : ""}{pickLocalized(e.title, locale)}
                      </div>
                    ))}
                    {evs.length > 2 && (
                      <div className="text-[0.6rem] font-caps" style={{ color: isSel ? "var(--gold)" : "var(--muted)" }}>
                        +{evs.length - 2}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day panel */}
        <div className="mt-10" data-testid="agenda-selected-panel">
          {selected ? (
            <>
              <div className="flex flex-wrap items-center gap-4">
                <h2 className="font-display text-2xl sm:text-3xl capitalize" style={{ color: "var(--ink)" }}>
                  {selectedLabel}
                  <span className="font-body text-sm ml-3" style={{ color: "var(--muted)" }}>
                    {selectedEvents.length > 0 ? `${selectedEvents.length} ${l.events}` : ""}
                  </span>
                </h2>
                {isFetesDay && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/presence/programme.pdf?locale=${locale}&day=${selected}`}
                    target="_blank" rel="noreferrer"
                    data-testid="agenda-day-pdf"
                    className="btn btn-primary"
                    style={{ padding: "0.5rem 1.1rem" }}
                  >
                    📄 {l.dayPdf}
                  </a>
                )}
              </div>
              {selectedEvents.length === 0 ? (
                <p className="font-body italic mt-4" style={{ color: "var(--muted)" }} data-testid="agenda-empty-day">{l.empty}</p>
              ) : (
                <div className="mt-6 space-y-3">
                  {selectedEvents.map((e) => (
                    <Link
                      key={`${e.id}-sel`}
                      to={`/evenements/${e.id}`}
                      data-testid={`agenda-event-${e.id}`}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 bg-white border p-4 transition-shadow hover:shadow-md"
                      style={{ borderColor: (e.tags || []).includes("fetes_2027") ? "var(--gold)" : "var(--border)" }}
                    >
                      <div className="font-caps text-[0.72rem] shrink-0 min-w-[110px]" style={{ color: "var(--cinnabar)", letterSpacing: "0.14em" }}>
                        {e.time ? `${e.time}${e.end_time ? `–${e.end_time}` : ""}` : "—"}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-lg sm:text-xl leading-tight" style={{ color: "var(--ink)" }}>
                          {pickLocalized(e.title, locale)}
                        </div>
                        {pickLocalized(e.location, locale) && (
                          <div className="font-body text-sm mt-0.5" style={{ color: "var(--muted)" }}>
                            {pickLocalized(e.location, locale)}
                          </div>
                        )}
                      </div>
                      {(e.tags || []).includes("fetes_2027") && (
                        <span className="tag tag-gold sm:ml-auto shrink-0">FÊTES 2027</span>
                      )}
                      {matchFetesLocation(pickLocalized(e.location, "fr")) && (
                        <button
                          data-testid={`agenda-event-map-${e.id}`}
                          title={l.onMap}
                          aria-label={l.onMap}
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            const m = matchFetesLocation(pickLocalized(e.location, "fr"));
                            setMapFocus({
                              ...m,
                              title: pickLocalized(e.title, locale),
                              subtitle: `${e.time ? `${e.time}${e.end_time ? `–${e.end_time}` : ""} · ` : ""}${pickLocalized(e.location, locale)}`,
                            });
                            setTimeout(() => document.getElementById("agenda-map-anchor")?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
                          }}
                          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border font-caps text-[0.6rem] transition-colors"
                          style={{ borderColor: "var(--gold)", color: "var(--cinnabar)", letterSpacing: "0.14em", marginLeft: (e.tags || []).includes("fetes_2027") ? 0 : "auto" }}
                        >
                          📍 {l.onMap.toUpperCase()}
                        </button>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="font-body italic" style={{ color: "var(--muted)" }}>{l.pick}</p>
          )}
        </div>

        {/* Map — event located on the Bay map after a pin click */}
        {mapFocus && (
          <div className="mt-10" id="agenda-map-anchor" data-testid="agenda-map-block">
            <h3 className="font-display text-xl sm:text-2xl mb-4" style={{ color: "var(--ink)" }}>
              {l.mapTitle}
            </h3>
            <FetesMap locale={locale} focus={mapFocus} />
          </div>
        )}

        {/* iCal subscribe */}
        <div className="mt-10">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/events.ics`}
            data-testid="agenda-ical-subscribe"
            className="btn btn-outline"
          >
            🗓 {t.events?.icalSubscribe || (locale === "fr" ? "S'abonner au calendrier" : "Subscribe to calendar")}
          </a>
        </div>
      </div>
    </div>
  );
}
